import React from "react";
import { ApolloCache, gql } from "@apollo/client";
import { Models } from "@hex-insights/app-modules";
import {
	ContractInstallment,
	CreateInvoiceForContractInstallmentMutation,
	graphTypeNames,
	Invoice,
	InvoiceDetailDocument,
	InvoiceDetailQuery,
	InvoiceDetailQueryVariables,
	useCreateInvoiceForContractInstallmentMutation,
} from "../../../../Utilities";

export type InvoiceCreateOnContractInstallmentButtonProps = {
	contractInstallmentID: ContractInstallment["id"];
} & Omit<
	Models.ActionButtonProps<CreateInvoiceForContractInstallmentMutation["createInvoiceForContractInstallment"]>,
	"performAction"
>;

export function InvoiceCreateOnContractInstallmentButton({
	contractInstallmentID,
	...props
}: InvoiceCreateOnContractInstallmentButtonProps) {
	const create = useCreate();
	const applyCreate = React.useCallback(() => {
		return create(contractInstallmentID);
	}, [create, contractInstallmentID]);

	return <Models.ActionButton performAction={applyCreate} {...props} />;
}

function useCreate() {
	const [createInvoice] = useCreateInvoiceForContractInstallmentMutation();

	return React.useCallback(
		async (contractInstallmentID: ContractInstallment["id"]) => {
			const { data, errors } = await createInvoice({
				variables: { contractInstallmentID },
				update: (cache, result) => {
					const createdObject = result.data?.createInvoiceForContractInstallment;
					if (!createdObject) {
						return;
					}

					cache.writeQuery<InvoiceDetailQuery, InvoiceDetailQueryVariables>({
						query: InvoiceDetailDocument,
						data: { invoice: createdObject },
						variables: { id: createdObject.id },
					});

					cache.evict({ id: "ROOT_QUERY", fieldName: "invoiceConnection" });
					cache.evict({ id: "ROOT_QUERY", fieldName: "invoiceLineItemConnection" });
					addToInvoicesOfContractInstallmentCache(cache, contractInstallmentID, createdObject);
				},
			});

			return { data: data?.createInvoiceForContractInstallment ?? null, errors: errors ?? null };
		},
		[createInvoice],
	);
}

function addToInvoicesOfContractInstallmentCache(
	cache: ApolloCache<any>,
	targetID: ContractInstallment["id"],
	object: Pick<Invoice, "id">,
) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.ContractInstallment }),
		fields: {
			invoices: (cachedValue) => [...cachedValue, objectRef],
		},
	});
}

function toCacheRef(cache: ApolloCache<any>, object: Pick<Invoice, "id">) {
	return (
		cache.writeFragment({
			fragment: gql`
				fragment InvoiceRef on Invoice {
					id
				}
			`,
			data: object,
		}) ?? null
	);
}
